import { usePersistentState } from '@/hooks/use-persistent-state'

/**
 * Per-device preference: open player-name links in a new browser tab (the full
 * `/player/{id}/view` page) instead of the in-app quick-preview card.
 *
 * Display chrome, not data → localStorage (this browser only), per the
 * `usePersistentState` convention. Both the settings toggle and `PlayerLink`
 * read through this hook so the key + default stay in one place. Takes effect on
 * a link's next render (i.e. next navigation) — no live cross-page propagation,
 * which is fine for a low-stakes view preference.
 */
export const PLAYER_LINK_NEW_TAB_KEY = 'csfbl.player.openInNewTab'

export function usePlayerLinkNewTab() {
  return usePersistentState<boolean>(PLAYER_LINK_NEW_TAB_KEY, false)
}

/**
 * Read the preference imperatively at call time (not via a hook), for the click
 * handler in the player-card launcher. Reading fresh from localStorage on each
 * click means the launcher — the single chokepoint every card open flows through
 * — always sees the current value, so the setting takes effect immediately on the
 * next click with no reload. Degrades to `false` (default = quick-preview card) if
 * localStorage is unavailable or the value can't be parsed.
 */
export function readPlayerLinkNewTab(): boolean {
  try {
    return localStorage.getItem(PLAYER_LINK_NEW_TAB_KEY) === 'true'
  } catch {
    return false
  }
}
